/**
 * Feature Flag Service
 * Manages feature toggles with support for:
 * - Environment variables (build-time defaults)
 * - LocalStorage (user session persistence)
 * - URL parameters (immediate testing)
 *
 * Effective priority (last write wins in constructor):
 * URL params > localStorage > env vars
 */

import { FEATURE_FLAGS_STORAGE_KEY } from "@/constants";

export interface FeatureFlags {
  newStoreGroupServiceEnabled: boolean;
}

export type FeatureFlagKey = keyof FeatureFlags;

/**
 * Maps feature flag keys to their URL shorthand names
 * When adding a new feature flag, add it here once and it will work everywhere
 */
const FEATURE_FLAG_CONFIG: Record<FeatureFlagKey, string> = {
  newStoreGroupServiceEnabled: "storeGroups",
};

/**
 * Reverse mapping from shorthand names to feature flag keys
 */
const createShorthandToKeyMap = (): Record<string, FeatureFlagKey> => {
  return Object.entries(FEATURE_FLAG_CONFIG).reduce(
    (acc, [key, value]) => {
      acc[value] = key as FeatureFlagKey;
      return acc;
    },
    {} as Record<string, FeatureFlagKey>,
  );
};

/**
 * Interface for feature flag services
 * Both DefaultFeatureFlagService and MockFeatureFlagService implement this
 */
export interface FeatureFlagService {
  /**
   * Check if a feature flag is enabled
   */
  isEnabled(flag: FeatureFlagKey): boolean;

  /**
   * Get all feature flags
   */
  getAll(): FeatureFlags;

  /**
   * Manually set a flag value
   */
  setFlag(flag: FeatureFlagKey, enabled: boolean): void;

  /**
   * Generate shareable URL with current flags enabled
   */
  getShareableUrl(): string;
}

export class DefaultFeatureFlagService implements FeatureFlagService {
  #flags: FeatureFlags;

  constructor() {
    // Start with environment defaults (base state)
    this.#flags = this.#loadFromEnv();

    // Merge with localStorage (user overrides)
    const stored = this.#loadFromStorage();
    if (stored) {
      this.#flags = { ...this.#flags, ...stored };
    }

    // Apply URL params for immediate testing
    this.#applyUrlParams();
  }

  /**
   * Load defaults from environment variables
   */
  #loadFromEnv(): FeatureFlags {
    return {
      newStoreGroupServiceEnabled:
        import.meta.env.VITE_NEW_STORE_GROUP_SERVICE_ENABLED === "true",
    };
  }

  /**
   * Load from localStorage
   */
  #loadFromStorage(): Partial<FeatureFlags> | null {
    try {
      const stored = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
      if (!stored) return null;

      return JSON.parse(stored) as Partial<FeatureFlags>;
    } catch (e) {
      console.warn("Failed to load feature flags from storage:", e);
      return null;
    }
  }

  /**
   * Apply URL parameters: ?ff=storeGroups
   * Special values:
   * - ?ff=* (enable all)
   * - ?ff= (disable all)
   */
  #applyUrlParams(): void {
    if (typeof window === "undefined") return;

    const ffParam = new URLSearchParams(window.location.search).get("ff");

    if (!ffParam && ffParam !== "") return;

    if (ffParam === "*") {
      this.#enableAllFlagsFromUrl();
      return;
    }

    if (ffParam === "") {
      this.#disableAllFlagsFromUrl();
      return;
    }

    this.#enableSelectedFlagsFromUrl(ffParam);
  }

  #enableAllFlagsFromUrl(): void {
    Object.keys(this.#flags).forEach((key) => {
      this.#flags[key as FeatureFlagKey] = true;
    });
    this.#persistToStorage();
  }

  #disableAllFlagsFromUrl(): void {
    Object.keys(this.#flags).forEach((key) => {
      this.#flags[key as FeatureFlagKey] = false;
    });
    localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
  }

  #enableSelectedFlagsFromUrl(ffParam: string): void {
    const enabledShorthands = ffParam
      .split(",")
      .map((f) => f.trim())
      .filter(Boolean);

    const shorthandToKeyMap = createShorthandToKeyMap();
    let changed = false;

    enabledShorthands.forEach((shorthand) => {
      const featureKey = shorthandToKeyMap[shorthand];
      if (featureKey && featureKey in this.#flags && !this.#flags[featureKey]) {
        this.#flags[featureKey] = true;
        changed = true;
      }
    });

    if (changed) {
      this.#persistToStorage();
    }
  }

  /**
   * Persist current flags to localStorage
   */
  #persistToStorage(): void {
    try {
      localStorage.setItem(
        FEATURE_FLAGS_STORAGE_KEY,
        JSON.stringify(this.#flags),
      );
    } catch (e) {
      console.warn("Failed to persist feature flags:", e);
    }
  }

  /**
   * Check if a feature flag is enabled
   */
  isEnabled(flag: FeatureFlagKey): boolean {
    return this.#flags[flag];
  }

  /**
   * Get all feature flags
   */
  getAll(): FeatureFlags {
    return { ...this.#flags };
  }

  /**
   * Manually set a flag value
   */
  setFlag(flag: FeatureFlagKey, enabled: boolean): void {
    this.#flags[flag] = enabled;
    this.#persistToStorage();
  }

  /**
   * Generate shareable URL with current flags enabled
   */
  getShareableUrl(): string {
    const enabledFlags = (Object.keys(this.#flags) as FeatureFlagKey[])
      .filter((key) => this.#flags[key])
      .map((key) => FEATURE_FLAG_CONFIG[key]);

    const baseUrl = window.location.origin + window.location.pathname;
    return enabledFlags.length > 0
      ? `${baseUrl}?ff=${enabledFlags.join(",")}`
      : baseUrl;
  }
}
